import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInAnonymously as firebaseSignInAnonymously,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  updateProfile,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';
import { isIOS, isSafari, isStandalonePWA } from '../../utils/platform';

// Firebase configuration - replace with actual values
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

/**
 * Initialize Firebase app
 */
export function initializeFirebase(): FirebaseApp {
  if (!app) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
  }
  return app;
}

/**
 * Get Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    initializeFirebase();
  }
  return auth!;
}

/**
 * Sign in with Google
 * 
 * iOS/Safari Strategy:
 * - Redirect flow is broken due to storage partitioning (ITP)
 * - Popup flow works if triggered by direct user interaction (tap)
 * - PWA/standalone mode: Neither works, show error message
 * 
 * Other browsers: Try popup, fall back to redirect if blocked
 */
export async function signInWithGoogle(): Promise<User> {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  
  // iOS PWA (standalone mode) cannot complete OAuth - neither popup nor redirect works
  if (isIOS() && isStandalonePWA()) {
    throw new Error('Google sign-in is not available when using "Add to Home Screen". Please open in Safari browser or continue as a guest.');
  }
  
  // Ensure persistence is set
  await setPersistence(auth, browserLocalPersistence);
  
  // iOS Safari: Must use popup (redirect fails due to storage partitioning)
  // Popup works because it's triggered by user tap (not programmatic)
  if (isIOS() || isSafari()) {
    try {
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (error: unknown) {
      if (error instanceof Error) {
        // User closed the popup - not a real error
        if (error.message.includes('popup-closed-by-user') || 
            error.message.includes('cancelled-popup-request')) {
          throw new Error('Sign-in was cancelled.');
        }
        // Popup was blocked - this shouldn't happen for user-initiated taps
        if (error.message.includes('popup') || error.message.includes('blocked')) {
          throw new Error('Pop-up was blocked. Please allow pop-ups for this site and try again.');
        }
      }
      throw error;
    }
  }
  
  // Other browsers: Try popup first, fall back to redirect if blocked
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error: unknown) {
    // If popup was blocked, fall back to redirect
    if (error instanceof Error && 
        (error.message.includes('popup') || error.message.includes('blocked'))) {
      await signInWithRedirect(auth, provider);
      throw new Error('Redirecting to Google...');
    }
    throw error;
  }
}

/**
 * Handle Google redirect result on page load
 * Call this in your app initialization to handle returning from Google auth
 * 
 * IMPORTANT: Only needed for non-iOS/Safari browsers that use redirect flow.
 * iOS/Safari use popup flow, so this should be skipped on those platforms.
 */
export async function handleGoogleRedirectResult(): Promise<User | null> {
  // Skip on iOS/Safari - we use popup flow there, not redirect
  // Calling getRedirectResult on these platforms causes "missing initial state" error
  if (isIOS() || isSafari()) {
    return null;
  }
  
  const auth = getFirebaseAuth();
  try {
    const result = await getRedirectResult(auth);
    if (result?.user) {
      // eslint-disable-next-line no-console
      console.log('[Auth] Redirect result: user authenticated', result.user.displayName);
    }
    return result?.user ?? null;
  } catch (error: unknown) {
    console.error('[Auth] Google redirect result error:', error);
    // Provide user-friendly error messages
    if (error instanceof Error) {
      if (error.message.includes('auth/credential-already-in-use')) {
        throw new Error('This Google account is already linked to another user.');
      }
      if (error.message.includes('auth/popup-closed-by-user') || 
          error.message.includes('auth/cancelled-popup-request')) {
        // User cancelled - not really an error
        return null;
      }
      if (error.message.includes('auth/network-request-failed')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      // "missing initial state" error - ignore, means no redirect was in progress
      if (error.message.includes('missing initial state')) {
        return null;
      }
      throw error;
    }
    throw new Error('Google sign-in failed. Please try again.');
  }
}

/**
 * Sign in anonymously with optional display name
 */
export async function signInAnonymously(displayName?: string): Promise<User> {
  const auth = getFirebaseAuth();
  const result = await firebaseSignInAnonymously(auth);
  
  // Set display name if provided
  if (displayName && result.user) {
    await updateProfile(result.user, { displayName });
  }
  
  return result.user;
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseSignOut(auth);
}

/**
 * Update current user's display name
 */
export async function setDisplayName(displayName: string): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('No user signed in');
  await updateProfile(user, { displayName });
}

/**
 * Get current user's ID token
 * @param forceRefresh - If true, forces token refresh from Firebase servers
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChanged(callback: (user: User | null) => void): () => void {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

/**
 * Format display name to "FirstName L" format
 */
export function formatDisplayName(fullName: string | null): string {
  if (!fullName) return 'Anonymous';
  
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0];
  return `${firstName} ${lastInitial}`;
}
